import { Reservation, EmailSettings, SiteSettings, CustomerMessage } from '../types';
import { getCustomerReference } from '../utils/referenceGenerator';

export const DEFAULT_EMAIL_SETTINGS: EmailSettings = {
  orgName: 'Uniform Loop Immingham',
  fromName: 'Uniform Loop Immingham',
  fromEmail: 'bert.boyden@virginmedia.com',
  senderEmail: 'bert.boyden@virginmedia.com',
  replyToAddress: 'bert.boyden@virginmedia.com',
  replyToEmail: 'bert.boyden@virginmedia.com',
  supportEmail: 'bert.boyden@virginmedia.com',
  contactPhone: '01469 570000',
  emailSignature: 'Warm regards,\nThe Uniform Loop Volunteer Team\nBert Boyden Community Centre',
  footerMessage: 'Uniform Loop Immingham is a community-led initiative based at Bert Boyden Community Centre, giving pre-loved school uniform a second life.',
  provider: 'smtp',
  smtpHost: 'smtp.virginmedia.com',
  smtpPort: 587,
  smtpEncryption: 'tls',
  smtpUser: 'bert.boyden@virginmedia.com',
  smtpPass: '',
  primaryColor: '#002366'
};

export function getBaseLayout(contentHtml: string, settings: EmailSettings): string {
  const year = new Date().getFullYear();
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${settings.orgName}</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f8fafc; margin: 0; padding: 0; color: #1e293b; }
    .container { max-width: 600px; margin: 20px auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05); }
    .header { background-color: #002366; padding: 28px 24px; text-align: center; color: #ffffff; }
    .header h1 { margin: 0; font-size: 24px; font-weight: 800; tracking: -0.02em; letter-spacing: -0.5px; }
    .header p { margin: 4px 0 0 0; font-size: 13px; color: #93c5fd; font-weight: 500; }
    .content { padding: 32px 24px; }
    .footer { background-color: #f1f5f9; padding: 20px 24px; text-align: center; font-size: 12px; color: #64748b; border-top: 1px solid #e2e8f0; }
    .badge { display: inline-block; padding: 6px 14px; background-color: #eff6ff; color: #1d4ed8; font-weight: 700; font-size: 13px; border-radius: 9999px; border: 1px solid #bfdbfe; font-family: monospace; }
    .card { background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin: 16px 0; }
    .item-table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    .item-table th, .item-table td { padding: 10px 12px; text-align: left; font-size: 13px; border-bottom: 1px solid #e2e8f0; }
    .item-table th { background-color: #f1f5f9; color: #475569; font-weight: 700; }
    .btn { display: inline-block; padding: 12px 24px; background-color: #2563eb; color: #ffffff; text-decoration: none; font-weight: 700; border-radius: 8px; margin-top: 16px; font-size: 14px; }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${settings.orgName}</h1>
      <p>Giving School Uniform a Second Life</p>
    </div>
    <div class="content">
      ${contentHtml}
      
      <div style="margin-top: 28px; padding-top: 16px; border-top: 1px solid #e2e8f0; font-size: 13px; color: #475569;">
        ${settings.emailSignature.replace(/\n/g, '<br>')}
      </div>
    </div>
    <div class="footer">
      <p style="margin: 0 0 6px 0; font-weight: 600; color: #334155;">${settings.orgName}</p>
      <p style="margin: 0 0 6px 0;">${settings.footerMessage}</p>
      <p style="margin: 0; font-size: 11px; color: #94a3b8;">Contact: ${settings.supportEmail} | Tel: ${settings.contactPhone}</p>
      <p style="margin: 6px 0 0 0; font-size: 10px; color: #cbd5e1;">© ${year} ${settings.orgName}. All rights reserved.</p>
    </div>
  </div>
</body>
</html>`;
}

export function renderReservationCreatedEmail(
  reservation: Reservation,
  settings: EmailSettings,
  siteSettings: SiteSettings
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `Your Uniform Loop Reservation - ${code}`;

  const itemsListHtml = (reservation.items || [])
    .map(
      (item) => `
      <tr>
        <td><strong>${item.productName}</strong><br><span style="color:#64748b; font-size:12px;">${item.schoolName || ''}</span></td>
        <td>${item.size || 'Standard'}</td>
        <td style="text-align: center;">${item.quantity}</td>
      </tr>`
    )
    .join('');

  const contentHtml = `
    <h2 style="margin-top:0; color:#0f172a; font-size: 20px;">Reservation Confirmation</h2>
    <p>Dear <strong>${reservation.parentName}</strong>,</p>
    <p>Thank you for using Uniform Loop! We have successfully received your reservation request for pre-loved school uniforms.</p>
    
    <div class="card" style="text-align: center;">
      <span style="font-size: 12px; color: #64748b; font-weight: 700; text-transform: uppercase;">Customer Reference</span><br>
      <div style="font-size: 24px; font-weight: 900; color: #002366; font-family: monospace; margin-top: 4px;">${code}</div>
    </div>

    <h3 style="font-size: 15px; color: #0f172a; margin-bottom: 8px;">Reserved Uniform Items</h3>
    <table class="item-table">
      <thead>
        <tr>
          <th>Item</th>
          <th>Size</th>
          <th style="text-align: center;">Qty</th>
        </tr>
      </thead>
      <tbody>
        ${itemsListHtml}
      </tbody>
    </table>

    <div class="card" style="margin-top: 20px; background-color: #eff6ff; border-color: #bfdbfe;">
      <h3 style="margin-top:0; font-size: 14px; color: #1e40af;">📍 Collection Information</h3>
      <p style="margin: 4px 0; font-size: 13px; color: #1e3a8a;"><strong>Collection Location:</strong> ${siteSettings.collectionLocation || 'Bert Boyden Community Centre'}</p>
      <p style="margin: 4px 0; font-size: 13px; color: #1e3a8a;"><strong>Address:</strong> ${siteSettings.collectionAddress || '34 Carver Road, Immingham DN40 1DS'}</p>
      <p style="margin: 4px 0; font-size: 13px; color: #1e3a8a;"><strong>Chosen Collection Date:</strong> <span style="font-size: 14px; font-weight: 800; color: #002366;">${reservation.preferredCollectionDay}</span></p>
      <p style="margin: 4px 0; font-size: 13px; color: #1e3a8a;"><strong>Hub Opening Times:</strong> ${siteSettings.openingHours || 'Tue, Thu, Sat 10:00 - 14:00'}</p>
    </div>

    <div class="card" style="margin-top: 16px; background-color: #f8fafc; text-align: center;">
      <h3 style="margin-top:0; font-size: 14px; color: #0f172a;">🛠️ Find &amp; Manage Your Reservation</h3>
      <p style="font-size: 13px; color: #475569; margin: 4px 0 12px 0;">
        Need to change your collection date, update details, leave a note for volunteers, or cancel?
      </p>
      <p style="font-size: 12px; color: #64748b; margin-bottom: 8px;">
        Use your Reference <strong>${code}</strong> and email address <strong>${reservation.email}</strong> on our website:
      </p>
      <a href="/manage-reservation" class="btn" style="background-color: #002366;">Find My Reservation</a>
    </div>

    <div style="margin-top: 16px; padding: 12px; border: 1px dashed #cbd5e1; border-radius: 8px; font-size: 12px; color: #64748b;">
      <strong>Cancellation & Changes:</strong> If you no longer require these items or cannot make your collection slot, please visit the Find My Reservation link above or reply to this email so we can return the items to stock for other families.
    </div>
  `;

  const text = `Dear ${reservation.parentName},\n\nThank you for reserving uniform with Uniform Loop!\n\nCustomer Reference: ${code}\nCollection Slot: ${reservation.preferredCollectionDay}\nCollection Location: ${siteSettings.collectionLocation}\nAddress: ${siteSettings.collectionAddress}\n\nOur volunteers are preparing your order.`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderBeingPreparedEmail(
  reservation: Reservation,
  settings: EmailSettings,
  siteSettings: SiteSettings
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `Your Uniform Order Is Being Prepared - ${code}`;

  const contentHtml = `
    <h2 style="margin-top:0; color:#002366; font-size: 20px;">📦 Order Being Prepared</h2>
    <p>Dear <strong>${reservation.parentName}</strong>,</p>
    <p>Our volunteers are currently locating and bagging your requested uniform items at the community hub.</p>
    
    <div class="card" style="text-align: center; background-color: #eff6ff; border-color: #bfdbfe;">
      <span style="font-size: 12px; color: #1e40af; font-weight: 700; text-transform: uppercase;">Customer Reference</span><br>
      <div style="font-size: 24px; font-weight: 900; color: #002366; font-family: monospace; margin-top: 4px;">${code}</div>
    </div>

    <div class="card" style="margin-top: 16px;">
      <h3 style="margin-top:0; font-size: 14px; color: #0f172a;">📍 Collection Details</h3>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Collection Date:</strong> ${reservation.preferredCollectionDay}</p>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Hub Location:</strong> ${siteSettings.collectionLocation || 'Bert Boyden Community Centre'}</p>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Address:</strong> ${siteSettings.collectionAddress || '34 Carver Road, Immingham DN40 1DS'}</p>
    </div>

    <p style="font-size: 13px; color: #334155; line-height: 1.5;">
      You will receive another notification as soon as your items are ready for collection!
    </p>
  `;

  const text = `Dear ${reservation.parentName},\n\nYour uniform reservation (${code}) is now being prepared by our volunteers.\nCollection Date: ${reservation.preferredCollectionDay}\nHub Location: ${siteSettings.collectionLocation}`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderReadyForCollectionEmail(
  reservation: Reservation,
  settings: EmailSettings,
  siteSettings: SiteSettings
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `Your Uniform Is Ready to Collect - ${code}`;

  const contentHtml = `
    <h2 style="margin-top:0; color:#15803d; font-size: 20px;">🎉 Your Order Is Ready for Collection!</h2>
    <p>Dear <strong>${reservation.parentName}</strong>,</p>
    <p>Great news! Our volunteers have packed and prepared your reserved uniform items.</p>
    
    <div class="card" style="text-align: center; background-color: #f0fdf4; border-color: #bbf7d0;">
      <span style="font-size: 12px; color: #166534; font-weight: 700; text-transform: uppercase;">Customer Reference</span><br>
      <div style="font-size: 24px; font-weight: 900; color: #15803d; font-family: monospace; margin-top: 4px;">${code}</div>
    </div>

    <div class="card" style="margin-top: 16px;">
      <h3 style="margin-top:0; font-size: 14px; color: #0f172a;">📍 Collection Point Details</h3>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Hub Location:</strong> ${siteSettings.collectionLocation || 'Community Uniform Hub'}</p>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Address:</strong> ${siteSettings.collectionAddress || 'High Street Hub'}</p>
      <p style="margin: 4px 0; font-size: 13px;"><strong>Opening Hours:</strong> ${siteSettings.openingHours || 'Mon-Fri 9am - 4pm'}</p>
    </div>

    <p style="font-size: 13px; color: #334155; line-height: 1.5;">
      <strong>Instructions:</strong> Simply show this email or state your reference (<strong>${code}</strong>) to a volunteer when you arrive.
    </p>
  `;

  const text = `Dear ${reservation.parentName},\n\nYour uniform reservation (${code}) is ready for collection at ${siteSettings.collectionLocation}.\n\nAddress: ${siteSettings.collectionAddress}\nHours: ${siteSettings.openingHours}`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderCollectedEmail(
  reservation: Reservation,
  settings: EmailSettings
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `Thank You for Collecting Your Uniform - ${code}`;

  const contentHtml = `
    <h2 style="margin-top:0; color:#15803d; font-size: 20px;">✅ Uniform Collection Confirmed</h2>
    <p>Dear <strong>${reservation.parentName}</strong>,</p>
    <p>This email confirms that your uniform reservation <strong>${code}</strong> has been collected.</p>
    
    <div class="card" style="background-color: #f0fdf4; border-color: #bbf7d0; color: #166534; font-size: 13px;">
      Thank you for choosing Uniform Loop and helping give pre-loved school uniforms a second life in our community!
    </div>
  `;

  const text = `Dear ${reservation.parentName},\n\nThank you for collecting your uniform reservation (${code}).\nUniform Loop Immingham`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderReservationCancelledEmail(
  reservation: Reservation,
  settings: EmailSettings
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `Uniform Loop Reservation Cancelled - ${code}`;

  const contentHtml = `
    <h2 style="margin-top:0; color:#b91c1c; font-size: 20px;">Reservation Cancelled</h2>
    <p>Dear <strong>${reservation.parentName}</strong>,</p>
    <p>This email confirms that your reservation <strong>${code}</strong> has been cancelled.</p>
    
    <div class="card" style="background-color: #fef2f2; border-color: #fecaca; color: #991b1b; font-size: 13px;">
      All items from this reservation have been safely returned to our active stock catalogue so other families in the community can access them.
    </div>

    <p style="font-size: 13px; color: #475569;">
      If you still require school uniforms, please visit our website to place a new reservation whenever you are ready.
    </p>
  `;

  const text = `Dear ${reservation.parentName},\n\nYour reservation (${code}) has been cancelled. The reserved items have been returned to available inventory.`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderContactAcknowledgementEmail(
  customerName: string,
  refNumber: string,
  userMessage: string,
  settings: EmailSettings
): { subject: string; html: string; text: string } {
  const subject = `Thank you for contacting ${settings.orgName} [Ref: ${refNumber}]`;

  const contentHtml = `
    <h2 style="margin-top:0; color:#0f172a; font-size: 20px;">Enquiry Received</h2>
    <p>Dear <strong>${customerName}</strong>,</p>
    <p>Thank you for contacting ${settings.orgName}. We have received your message and a volunteer will respond as soon as possible.</p>
    
    <div class="card" style="text-align: center; background-color: #f8fafc;">
      <span style="font-size: 12px; color: #64748b; font-weight: 700; text-transform: uppercase;">Enquiry Reference Number</span><br>
      <div style="font-size: 20px; font-weight: 800; color: #002366; font-family: monospace; margin-top: 4px;">${refNumber}</div>
    </div>

    <div class="card" style="font-size: 13px; color: #334155;">
      <strong>Your Message:</strong><br>
      <em style="color: #64748b;">"${userMessage}"</em>
    </div>

    <p style="font-size: 13px; color: #475569;">
      Our team of volunteers operates during community hub opening hours. You will receive a direct email reply as soon as we review your enquiry.
    </p>
  `;

  const text = `Thank you for contacting ${settings.orgName}.\n\nReference Number: ${refNumber}\n\nWe have received your enquiry and a volunteer will respond shortly.`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderVolunteerReplyEmail(
  recipientName: string,
  volunteerName: string,
  replyBody: string,
  originalSubject: string,
  settings: EmailSettings,
  replyUrl?: string,
  referenceNumber?: string
): { subject: string; html: string; text: string } {
  const subject = originalSubject.startsWith('Re:') ? originalSubject : `Re: ${originalSubject}`;
  const refDisplay = referenceNumber ? referenceNumber.trim() : '';

  const contentHtml = `
    <h2 style="margin-top:0; color:#002366; font-size: 18px;">Response from ${settings.orgName}</h2>
    ${refDisplay ? `
    <p style="font-size: 13px; color: #475569; margin: 4px 0 14px 0;">
      Enquiry Reference: <strong style="font-family: monospace; color: #002366; font-size: 14px;">${refDisplay}</strong>
    </p>
    ` : ''}
    <p>Dear <strong>${recipientName}</strong>,</p>
    
    <div class="card" style="background-color: #ffffff; border-left: 4px solid #2563eb; padding: 18px; font-size: 14px; line-height: 1.6; color: #1e293b;">
      ${replyBody.replace(/\n/g, '<br>')}
    </div>

    <p style="font-size: 12px; color: #64748b; margin-top: 14px;">
      Sent by the Uniform Loop Volunteer Team
    </p>

    ${replyUrl ? `
    <div style="margin-top: 24px; padding: 20px; background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; text-align: center;">
      <p style="margin-top: 0; margin-bottom: 12px; font-size: 13px; color: #334155; font-weight: 600;">
        If you need to reply to this message, use the button below:
      </p>
      <a href="${replyUrl}" class="btn" style="background-color: #002366; color: #ffffff; padding: 12px 28px; font-weight: bold; border-radius: 10px; display: inline-block; text-decoration: none; font-size: 14px; box-shadow: 0 2px 4px rgba(0, 35, 102, 0.15);">
        REPLY TO UNIFORM LOOP
      </a>
      ${refDisplay ? `
      <p style="margin-bottom: 0; margin-top: 12px; font-size: 11px; color: #64748b;">
        Your enquiry reference: <strong style="font-family: monospace;">${refDisplay}</strong>
      </p>
      ` : ''}
    </div>
    ` : ''}
  `;

  const textLines = [
    `Dear ${recipientName},`,
    '',
    refDisplay ? `Reference: ${refDisplay}` : '',
    '',
    replyBody,
    '',
    'Warm regards,',
    'The Uniform Loop Volunteer Team',
    settings.orgName,
    '',
    replyUrl ? `If you need to reply to this message, use the link below:\n${replyUrl}${refDisplay ? `\n\nYour enquiry reference: ${refDisplay}` : ''}` : ''
  ].filter((line) => line !== undefined);

  return { subject, html: getBaseLayout(contentHtml, settings), text: textLines.join('\n') };
}

export function renderCollectionDateUpdatedEmail(
  reservation: Reservation,
  decision: 'Approved' | 'Declined' | 'Rejected',
  decisionNotes: string | undefined,
  settings: EmailSettings
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const isApproved = decision === 'Approved';
  const subject = `Collection Date Request ${decision} - ${code}`;

  const contentHtml = `
    <h2 style="margin-top:0; color:${isApproved ? '#15803d' : '#b91c1c'}; font-size: 20px;">
      Collection Date Request ${decision}
    </h2>
    <p>Dear <strong>${reservation.parentName}</strong>,</p>
    <p>Your request to change the collection date for reservation <strong>${code}</strong> has been <strong>${decision.toLowerCase()}</strong> by our volunteer team.</p>
    
    <div class="card" style="border-left: 4px solid ${isApproved ? '#22c55e' : '#ef4444'};">
      <strong>${isApproved ? 'New Collection Date:' : 'Confirmed Collection Date:'}</strong> <span style="font-size:16px; font-weight:800; color:#0f172a;">${reservation.preferredCollectionDay}</span>
      ${decisionNotes ? `<br><br><strong>Volunteer Note:</strong> <em>"${decisionNotes}"</em>` : ''}
    </div>

    <p style="font-size:13px; color:#475569;">
      You can view and manage your reservation at any time on the <strong>Find My Reservation</strong> page using your reference <strong>${code}</strong> and email address.
    </p>
  `;

  const text = `Dear ${reservation.parentName},\n\nYour collection date request for ${code} has been ${decision.toLowerCase()}.\n${isApproved ? 'New Date:' : 'Collection Date:'} ${reservation.preferredCollectionDay}\n${decisionNotes ? `Note: ${decisionNotes}\n` : ''}\nUniform Loop Immingham`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

export function renderTestEmail(
  recipientEmail: string,
  settings: EmailSettings
): { subject: string; html: string; text: string } {
  const subject = `Test Email from ${settings.orgName} Email System`;

  const contentHtml = `
    <h2 style="margin-top:0; color:#166534; font-size: 20px;">✅ Email System Test Successful</h2>
    <p>Hello!</p>
    <p>This is a test email dispatched from the <strong>${settings.orgName}</strong> production SMTP module.</p>
    
    <div class="card" style="background-color: #f0fdf4; border-color: #bbf7d0; font-size: 13px; color: #166534;">
      <strong>Configuration Details:</strong><br>
      • Sender Name: ${settings.fromName}<br>
      • From Address: ${settings.fromEmail}<br>
      • Provider: SMTP Gateway (${settings.smtpHost || 'smtp.virginmedia.com'})<br>
      • Timestamp: ${new Date().toLocaleString('en-GB')}
    </div>

    <p style="font-size: 13px; color: #475569;">
      If you are receiving this email, your SMTP server settings, credentials, and HTML layout template are configured and functioning correctly.
    </p>
  `;

  const text = `Test email from ${settings.orgName} Email System.\n\nConfiguration is active and working!`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

/**
 * Render Volunteer Notification Email: New Uniform Order Received
 */
export function renderVolunteerNewOrderEmail(
  reservation: Reservation,
  settings: EmailSettings,
  volunteerName = 'Volunteer',
  portalUrl = 'https://together-uniforms.pages.dev/'
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `New Uniform Order Received - ${code}`;

  const itemsListHtml = (reservation.items || [])
    .map(
      (item) => `
      <tr>
        <td><strong>${item.productName}</strong><br><span style="color:#64748b; font-size:12px;">${item.schoolName || ''}</span></td>
        <td>${item.size || 'Standard'}</td>
        <td style="text-align: center; font-weight: bold;">${item.quantity}</td>
      </tr>`
    )
    .join('');

  const contentHtml = `
    <div style="display: inline-block; padding: 4px 10px; background-color: #eff6ff; color: #1d4ed8; font-size: 11px; font-weight: 800; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
      🔔 New Volunteer Notification
    </div>
    <h2 style="margin-top:0; color:#002366; font-size: 20px; font-weight: 800;">
      New Uniform Order Received
    </h2>
    <p>Hello <strong>${volunteerName}</strong>,</p>
    <p>A new customer reservation has just been placed and is waiting in the volunteer order queue.</p>
    
    <div class="card" style="border-left: 4px solid #2563eb; background-color: #f8fafc;">
      <div style="font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase;">Order Reference</div>
      <div style="font-size: 22px; font-weight: 900; color: #002366; font-family: monospace; margin: 2px 0 8px 0;">${code}</div>
      <div style="font-size: 13px; color: #334155; line-height: 1.6;">
        • <strong>Customer:</strong> ${reservation.parentName}<br>
        • <strong>Email:</strong> ${reservation.email}<br>
        • <strong>Phone:</strong> ${reservation.phone || 'Not provided'}<br>
        • <strong>Preferred Collection Day:</strong> <span style="color: #002366; font-weight: 700;">${reservation.preferredCollectionDay}</span>
        ${reservation.childFirstName ? `<br>• <strong>Child Name / School:</strong> ${reservation.childFirstName} ${reservation.schoolName ? `(${reservation.schoolName})` : ''}` : ''}
        ${reservation.optionalMessage ? `<br><br><strong>Customer Note:</strong> <em>"${reservation.optionalMessage}"</em>` : ''}
      </div>
    </div>

    <h3 style="font-size: 14px; color: #0f172a; margin-top: 20px; margin-bottom: 8px;">Reserved Items Summary</h3>
    <table class="item-table">
      <thead>
        <tr>
          <th>Item</th>
          <th>Size</th>
          <th style="text-align: center;">Qty</th>
        </tr>
      </thead>
      <tbody>
        ${itemsListHtml}
      </tbody>
    </table>

    <div style="text-align: center; margin-top: 24px;">
      <a href="${portalUrl}" class="btn" style="background-color: #002366; color: #ffffff; padding: 12px 28px; font-weight: bold; border-radius: 10px; display: inline-block; text-decoration: none;">
        Open Order in Admin Portal →
      </a>
    </div>

    <div style="margin-top: 24px; padding: 12px 16px; background-color: #f1f5f9; border-radius: 8px; font-size: 11px; color: #64748b; line-height: 1.5;">
      ℹ️ <strong>Why did I receive this email?</strong> You are registered as an active volunteer with <em>Reservations & Orders</em> access and have <em>New Order</em> email notifications enabled in your account preferences.
    </div>
  `;

  const text = `New Uniform Order Received - ${code}\nCustomer: ${reservation.parentName}\nEmail: ${reservation.email}\nCollection: ${reservation.preferredCollectionDay}\n\nOpen order in admin portal: ${portalUrl}\n\nUniform Loop Immingham Volunteer Notification`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

/**
 * Render Volunteer Notification Email: Uniform Order Updated
 */
export function renderVolunteerOrderUpdateEmail(
  reservation: Reservation,
  updateTitle: string,
  updateDetails: string,
  settings: EmailSettings,
  volunteerName = 'Volunteer',
  portalUrl = 'https://together-uniforms.pages.dev/'
): { subject: string; html: string; text: string } {
  const code = getCustomerReference(reservation);
  const subject = `Uniform Order Updated - ${code}`;

  const contentHtml = `
    <div style="display: inline-block; padding: 4px 10px; background-color: #fef3c7; color: #b45309; font-size: 11px; font-weight: 800; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
      🔄 Order Update Notification
    </div>
    <h2 style="margin-top:0; color:#002366; font-size: 20px; font-weight: 800;">
      Uniform Order Updated
    </h2>
    <p>Hello <strong>${volunteerName}</strong>,</p>
    <p>An update has occurred on reservation <strong>${code}</strong> (${reservation.parentName}).</p>
    
    <div class="card" style="border-left: 4px solid #f59e0b; background-color: #fffbeb;">
      <div style="font-size: 11px; color: #92400e; font-weight: 700; text-transform: uppercase;">Update Summary</div>
      <div style="font-size: 16px; font-weight: 800; color: #78350f; margin: 4px 0 8px 0;">${updateTitle}</div>
      <div style="font-size: 13px; color: #451a03; line-height: 1.6;">
        ${updateDetails}
      </div>
    </div>

    <div class="card" style="background-color: #f8fafc; font-size: 13px; color: #334155; line-height: 1.6;">
      • <strong>Order Code:</strong> <span style="font-family: monospace; font-weight: 700; color: #002366;">${code}</span><br>
      • <strong>Customer:</strong> ${reservation.parentName}<br>
      • <strong>Current Status:</strong> <span style="font-weight: 700;">${reservation.status}</span><br>
      • <strong>Collection Slot:</strong> ${reservation.preferredCollectionDay}
    </div>

    <div style="text-align: center; margin-top: 24px;">
      <a href="${portalUrl}" class="btn" style="background-color: #002366; color: #ffffff; padding: 12px 28px; font-weight: bold; border-radius: 10px; display: inline-block; text-decoration: none;">
        View Order in Admin Portal →
      </a>
    </div>

    <div style="margin-top: 24px; padding: 12px 16px; background-color: #f1f5f9; border-radius: 8px; font-size: 11px; color: #64748b; line-height: 1.5;">
      ℹ️ <strong>Why did I receive this email?</strong> You are registered as an active volunteer with <em>Reservations & Orders</em> access and have <em>Order Updates</em> email notifications enabled in your account preferences.
    </div>
  `;

  const text = `Uniform Order Updated - ${code}\nCustomer: ${reservation.parentName}\nUpdate: ${updateTitle}\n${updateDetails}\n\nView in admin portal: ${portalUrl}\n\nUniform Loop Immingham Volunteer Notification`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

/**
 * Render Volunteer Notification Email: New Customer Message Received
 */
export function renderVolunteerNewMessageEmail(
  message: CustomerMessage,
  settings: EmailSettings,
  volunteerName = 'Volunteer',
  portalUrl = 'https://together-uniforms.pages.dev/'
): { subject: string; html: string; text: string } {
  const refNum = message.referenceNumber || `UL-MSG-${message.id?.substring(0, 6).toUpperCase() || 'ENQUIRY'}`;
  const subject = `New Customer Message Received - ${refNum}`;

  const contentHtml = `
    <div style="display: inline-block; padding: 4px 10px; background-color: #f0fdf4; color: #166534; font-size: 11px; font-weight: 800; border-radius: 9999px; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 8px;">
      💬 New Customer Message
    </div>
    <h2 style="margin-top:0; color:#002366; font-size: 20px; font-weight: 800;">
      New Customer Message Received
    </h2>
    <p>Hello <strong>${volunteerName}</strong>,</p>
    <p>A community member has submitted an enquiry through the Uniform Loop contact form.</p>
    
    <div class="card" style="border-left: 4px solid #16a34a; background-color: #f8fafc;">
      <div style="font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase;">Message Reference</div>
      <div style="font-size: 20px; font-weight: 900; color: #002366; font-family: monospace; margin: 2px 0 8px 0;">${refNum}</div>
      <div style="font-size: 13px; color: #334155; line-height: 1.6;">
        • <strong>From:</strong> ${message.customerName}<br>
        • <strong>Email:</strong> ${message.email}<br>
        • <strong>Phone:</strong> ${message.phone || 'Not provided'}<br>
        • <strong>Subject:</strong> <strong>${message.subject}</strong>
      </div>
    </div>

    <div class="card" style="background-color: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px; margin: 16px 0;">
      <div style="font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase; margin-bottom: 6px;">Customer Message Preview</div>
      <div style="font-size: 13px; color: #1e293b; line-height: 1.6; white-space: pre-wrap; font-style: italic; background: #f8fafc; padding: 12px; border-radius: 8px;">"${message.message}"</div>
    </div>

    <div style="text-align: center; margin-top: 24px;">
      <a href="${portalUrl}" class="btn" style="background-color: #002366; color: #ffffff; padding: 12px 28px; font-weight: bold; border-radius: 10px; display: inline-block; text-decoration: none;">
        Open Message in Admin Portal →
      </a>
    </div>

    <div style="margin-top: 24px; padding: 12px 16px; background-color: #f1f5f9; border-radius: 8px; font-size: 11px; color: #64748b; line-height: 1.5;">
      ℹ️ <strong>Why did I receive this email?</strong> You are registered as an active volunteer with <em>Customer Messages</em> access and have <em>New Customer Message</em> email notifications enabled in your account preferences.
    </div>
  `;

  const text = `New Customer Message Received - ${refNum}\nFrom: ${message.customerName} (${message.email})\nSubject: ${message.subject}\n\n"${message.message}"\n\nOpen in admin portal: ${portalUrl}\n\nUniform Loop Immingham Volunteer Notification`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

/**
 * 13. WELCOME NEW USER ACCOUNT EMAIL
 * Sent to newly provisioned volunteers and administrators with a temporary password.
 * Informs the user of the 48-hour expiration and mandatory first-login password creation.
 */
export function renderWelcomeNewUserEmail(
  params: {
    displayName: string;
    email: string;
    temporaryPassword: string;
    portalUrl?: string;
    roleTitle?: string;
    expiresInHours?: number;
  },
  settings: EmailSettings = DEFAULT_EMAIL_SETTINGS
): { subject: string; html: string; text: string } {
  const portalUrl = params.portalUrl || 'https://uniformloop.org.uk/admin';
  const hours = params.expiresInHours || 48;
  const subject = `Welcome to Uniform Loop - Your Account Setup Details`;
  const firstName = params.displayName.split(' ')[0] || params.displayName;

  const contentHtml = `
    <div style="text-align: center; margin-bottom: 24px;">
      <span class="badge" style="background-color: #fef3c7; color: #92400e; border-color: #fde68a;">
        Account Onboarding
      </span>
      <h2 style="margin: 12px 0 6px 0; font-size: 20px; font-weight: 800; color: #002366;">
        Welcome to the Uniform Loop Team, ${firstName}!
      </h2>
      <p style="margin: 0; font-size: 14px; color: #475569;">
        An administrator has created your staff/volunteer access account for Uniform Loop.
      </p>
    </div>

    <div class="card" style="background-color: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 20px; margin: 20px 0;">
      <div style="font-size: 12px; font-weight: 700; color: #002366; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 12px;">
        Your Login Credentials
      </div>
      <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
        <tr>
          <td style="padding: 6px 0; color: #64748b; width: 140px; font-weight: 600;">Sign-In Email:</td>
          <td style="padding: 6px 0; color: #0f172a; font-weight: 700;">${params.email}</td>
        </tr>
        ${params.roleTitle ? `
        <tr>
          <td style="padding: 6px 0; color: #64748b; font-weight: 600;">Assigned Role:</td>
          <td style="padding: 6px 0; color: #0f172a;">${params.roleTitle}</td>
        </tr>
        ` : ''}
        <tr>
          <td style="padding: 6px 0; color: #64748b; font-weight: 600;">Temporary Password:</td>
          <td style="padding: 6px 0;">
            <code style="background-color: #002366; color: #ffffff; padding: 5px 12px; border-radius: 6px; font-family: monospace; font-size: 15px; font-weight: 700; letter-spacing: 1px;">
              ${params.temporaryPassword}
            </code>
          </td>
        </tr>
      </table>
    </div>

    <div style="background-color: #eff6ff; border-left: 4px solid #2563eb; border-radius: 8px; padding: 14px 16px; margin: 20px 0; font-size: 13px; color: #1e40af; line-height: 1.5;">
      <p style="margin: 0 0 6px 0; font-weight: 700;">
        ⚠️ Important Security Instructions
      </p>
      <ul style="margin: 0; padding-left: 18px; space-y: 4px;">
        <li>This temporary password is valid for <strong>${hours} hours</strong> only.</li>
        <li>When you first sign in, you will be <strong>required to create your own permanent password</strong> before gaining access.</li>
        <li>Administrators cannot view or recover your personal password.</li>
      </ul>
    </div>

    <div style="text-align: center; margin: 28px 0 16px 0;">
      <a href="${portalUrl}" class="btn" style="background-color: #002366; color: #ffffff; padding: 14px 32px; font-weight: 700; border-radius: 10px; display: inline-block; text-decoration: none; font-size: 15px; box-shadow: 0 4px 6px -1px rgba(0, 35, 102, 0.2);">
        Sign In to Uniform Loop &rarr;
      </a>
      <p style="margin: 10px 0 0 0; font-size: 12px; color: #64748b;">
        Or navigate to: <a href="${portalUrl}" style="color: #2563eb;">${portalUrl}</a>
      </p>
    </div>

    <div style="margin-top: 24px; padding: 12px 16px; background-color: #f1f5f9; border-radius: 8px; font-size: 11px; color: #64748b; line-height: 1.5;">
      ℹ️ If you did not expect this invitation or believe it was sent in error, please contact your team coordinator or reply directly to this email.
    </div>
  `;

  const text = `Welcome to Uniform Loop, ${firstName}!\n\nAn administrator has created your volunteer/staff account for Uniform Loop.\n\nYour Login Credentials:\n- Email: ${params.email}\n- Temporary Password: ${params.temporaryPassword}\n\nIMPORTANT SECURITY INSTRUCTIONS:\n- This temporary password expires in ${hours} hours.\n- You will be required to create your own secure password upon your first sign-in.\n- Administrators never have access to your personal password.\n\nSign in here: ${portalUrl}\n\nWarm regards,\nThe Uniform Loop Team`;

  return { subject, html: getBaseLayout(contentHtml, settings), text };
}

